/**
 * Presentation helpers for Tailwind classes, local dates/times, timeline dimensions, rate-limited
 * callbacks, and priority/energy styling.
 *
 * @module apps/web/src/lib/utils
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge conditional class values and resolve conflicting Tailwind utilities.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a Date as an en-US local 12-hour time with minutes.
 */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a Date as an en-US local short weekday, month, and day.
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Display a minute count as minutes or hours plus remaining minutes.
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
}

/**
 * Return minutes since local midnight, used as pixels at the base one-pixel-per-minute timeline scale.
 */
export function getTimeSlotPosition(time: Date): number {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  return hours * 60 + minutes;
}

/**
 * Return duration in pixels at the base one-pixel-per-minute timeline scale.
 */
export function getTimeSlotHeight(durationMinutes: number): number {
  return durationMinutes; // 1 minute = 1px
}

/**
 * Generate a browser cryptographic UUID.
 */
export function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Return a trailing callback that resets its timer on each invocation; wait is in milliseconds.
 */
export function debounce<Args extends unknown[]>(
  func: (...args: Args) => unknown,
  wait: number
): (...args: Args) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Return a leading-only callback that ignores further calls for limit milliseconds.
 */
export function throttle<Args extends unknown[]>(
  func: (...args: Args) => unknown,
  limit: number
): (...args: Args) => void {
  let inThrottle: boolean;
  return (...args: Args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Tailwind background/text classes keyed by named activity priority.
 */
export const priorityColors = {
  /** Badge background/text classes for urgent activity priority. */
  urgent: 'bg-priority-urgent text-white',
  /** Badge background/text classes for high activity priority. */
  high: 'bg-priority-high text-white',
  /** Badge background/text classes for medium activity priority. */
  medium: 'bg-priority-medium text-black',
  /** Badge background/text classes for low activity priority. */
  low: 'bg-priority-low text-white',
};

/**
 * Tailwind left-border classes keyed by named activity priority.
 */
export const priorityBorderColors = {
  /** Border classes for urgent activity priority. */
  urgent: 'border-l-priority-urgent',
  /** Border classes for high activity priority. */
  high: 'border-l-priority-high',
  /** Border classes for medium activity priority. */
  medium: 'border-l-priority-medium',
  /** Border classes for low activity priority. */
  low: 'border-l-priority-low',
};

/**
 * Tailwind foreground/background classes for supported activity lifecycle states.
 */
export const statusColors = {
  /** Badge appearance for activities that have not started. */
  todo: 'bg-muted text-muted-foreground',
  /** Badge appearance for activities currently underway. */
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  /** Badge appearance for completed activities. */
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  /** Badge appearance for cancelled activities. */
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

/**
 * Map a numeric energy level to low, medium, or high theme colors.
 */
export function getEnergyColor(level: number): string {
  if (level <= 2) return 'hsl(var(--energy-low))';
  if (level <= 3) return 'hsl(var(--energy-medium))';
  return 'hsl(var(--energy-high))';
}

/**
 * Split an HH:MM string into numeric hours/minutes; missing fields default to zero without range validation.
 */
export function parseTimeString(time: string): { /** Parsed hour component from the supplied clock-time string. */ hours: number; /** Parsed minute component from the supplied clock-time string. */ minutes: number } {
  const [hours, minutes] = time.split(':').map(Number);
  return { hours: hours ?? 0, minutes: minutes ?? 0 };
}

/**
 * Copy baseDate and set its local hours/minutes from a clock string, clearing seconds and milliseconds.
 */
export function timeStringToDate(time: string, baseDate: Date = new Date()): Date {
  const { hours, minutes } = parseTimeString(time);
  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Compare a date with today using local calendar year, month, and day.
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Compare two dates by local calendar year, month, and day.
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

/**
 * Map numeric priorities 1–3 to urgent/high/medium colors and all others to low.
 */
export function getPriorityColor(priority: number): string {
  if (priority === 1) return 'hsl(var(--priority-urgent))';
  if (priority === 2) return 'hsl(var(--priority-high))';
  if (priority === 3) return 'hsl(var(--priority-medium))';
  return 'hsl(var(--priority-low))';
}

/**
 * Format a full en-US local weekday/date heading.
 */
export function formatDateHeader(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
